import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '../../../environment/environment';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';

interface PriceExtraOptionRow {
  id: number;
  organizations_id?: number;
  label: string;
  is_active: boolean;
  sort_order?: number;
  default_price?: number | null;
}

@Component({
  selector: 'app-duna-event-price-extra-options',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatDialogModule,
  ],
  templateUrl: './duna-event-price-extra-options.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-event-price-extra-options.component.scss',
})
export class DunaEventPriceExtraOptionsComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['label', 'default_price', 'active', 'actions'];
  readonly tableDataSource = new MatTableDataSource<PriceExtraOptionRow>([]);
  labelDraft: Record<number, string> = {};
  priceDraft: Record<number, number | null> = {};
  loading = false;
  creating = false;
  savingLabelId: number | null = null;
  savingPriceId: number | null = null;
  togglingId: number | null = null;
  deletingId: number | null = null;
  newLabel = '';
  newPrice: number | null = null;
  errorMessage: string | null = null;

  private destroy$ = new Subject<void>();
  private readonly apiBase = `${environment.apiUrl}/events/price-extra-options`;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.reloadCatalogFromApi$()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        error: (error) => {
          this.tableDataSource.data = [];
          this.labelDraft = {};
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke hente price-extras.'
          );
        },
      });
  }

  private reloadCatalogFromApi$(): Observable<PriceExtraOptionRow[]> {
    return this.http.get<unknown>(this.apiBase).pipe(
      map((res) => {
        const rows = this.parseListResponse(res);
        const sorted = [...rows].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
        );
        this.tableDataSource.data = sorted;
        this.syncDrafts();
        return sorted;
      })
    );
  }

  private parseListResponse(res: unknown): PriceExtraOptionRow[] {
    if (res == null) {
      return [];
    }
    if (Array.isArray(res)) {
      return res
        .map((x) => this.mapApiRow(x))
        .filter((x): x is PriceExtraOptionRow => x != null);
    }
    if (typeof res !== 'object') {
      return [];
    }
    const o = res as Record<string, unknown>;
    const inner = o['items'] ?? o['data'] ?? o['results'];
    if (Array.isArray(inner)) {
      return inner
        .map((x) => this.mapApiRow(x))
        .filter((x): x is PriceExtraOptionRow => x != null);
    }
    return [];
  }

  private mapApiRow(raw: unknown): PriceExtraOptionRow | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const o = raw as Record<string, unknown>;
    const id = Number(o['id']);
    if (!Number.isFinite(id) || id <= 0) {
      return null;
    }
    return {
      id,
      organizations_id:
        o['organizations_id'] != null
          ? Number(o['organizations_id'])
          : undefined,
      label: String(o['label'] ?? ''),
      is_active: this.coerceBool(o['is_active']),
      sort_order: Number(o['sort_order'] ?? 0),
      default_price:
        o['default_price'] != null && o['default_price'] !== ''
          ? Number(o['default_price'])
          : null,
    };
  }

  private coerceBool(v: unknown): boolean {
    return v === true || v === 1 || v === '1' || v === 'true';
  }

  private syncDrafts(): void {
    const nextLabel: Record<number, string> = {};
    const nextPrice: Record<number, number | null> = {};
    this.tableDataSource.data.forEach((r) => {
      nextLabel[r.id] = r.label;
      nextPrice[r.id] = r.default_price ?? null;
    });
    this.labelDraft = nextLabel;
    this.priceDraft = nextPrice;
  }

  private replaceRowInTable(
    updated: unknown,
    fallbackId: number,
    labelFallback?: string
  ): void {
    const merged = this.mapApiRow(updated);
    const data = [...this.tableDataSource.data];
    const idx = data.findIndex((x) => x.id === (merged?.id ?? fallbackId));
    if (idx < 0) {
      return;
    }
    if (merged) {
      data[idx] = { ...data[idx], ...merged };
    } else if (labelFallback != null) {
      data[idx] = { ...data[idx], label: labelFallback };
    }
    this.tableDataSource.data = data;
    this.syncDrafts();
  }

  labelDirty(row: PriceExtraOptionRow): boolean {
    return (this.labelDraft[row.id] ?? '').trim() !== (row.label || '').trim();
  }

  priceDirty(row: PriceExtraOptionRow): boolean {
    const draft = this.priceDraft[row.id];
    const current = row.default_price ?? null;
    if (draft == null && current == null) return false;
    return Number(draft ?? 0) !== Number(current ?? 0);
  }

  savePrice(row: PriceExtraOptionRow): void {
    if (!this.priceDirty(row)) return;
    const next = this.priceDraft[row.id];
    this.savingPriceId = row.id;
    this.errorMessage = null;
    this.http
      .patch<unknown>(`${this.apiBase}/${row.id}`, {
        default_price:
          next == null || Number.isNaN(Number(next))
            ? null
            : Math.max(0, Number(next)),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.savingPriceId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (updated) => this.replaceRowInTable(updated, row.id),
        error: (error) => {
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke opdatere pris.'
          );
        },
      });
  }

  saveLabel(row: PriceExtraOptionRow): void {
    const next = (this.labelDraft[row.id] ?? '').trim();
    if (!next) {
      this.errorMessage = 'Navn må ikke være tomt.';
      return;
    }
    if (!this.labelDirty(row)) return;
    this.savingLabelId = row.id;
    this.errorMessage = null;
    this.http
      .patch<unknown>(`${this.apiBase}/${row.id}`, { label: next })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.savingLabelId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (updated) => {
          this.replaceRowInTable(updated, row.id, next);
          this.labelDraft[row.id] =
            this.tableDataSource.data.find((x) => x.id === row.id)?.label ??
            next;
        },
        error: (error) => {
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke opdatere navn.'
          );
        },
      });
  }

  onActiveChange(row: PriceExtraOptionRow, ev: MatSlideToggleChange): void {
    const next = ev.checked;
    this.togglingId = row.id;
    this.errorMessage = null;
    this.http
      .patch<unknown>(`${this.apiBase}/${row.id}`, { is_active: next })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.togglingId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (updated) => {
          this.replaceRowInTable(updated, row.id);
        },
        error: (error) => {
          ev.source.checked = !next;
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke ændre aktiv status.'
          );
        },
      });
  }

  createOption(): void {
    const label = this.newLabel.trim();
    if (!label) {
      this.errorMessage = 'Angiv et navn for den nye ekstra.';
      return;
    }
    this.creating = true;
    this.errorMessage = null;
    this.http
      .post<unknown>(this.apiBase, {
        label,
        default_price:
          this.newPrice != null && !Number.isNaN(Number(this.newPrice))
            ? Math.max(0, Number(this.newPrice))
            : null,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.reloadCatalogFromApi$()),
        finalize(() => {
          this.creating = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          this.newLabel = '';
          this.newPrice = null;
          this.errorMessage = null;
        },
        error: (error) => {
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke oprette price-extra.'
          );
        },
      });
  }

  deleteOption(row: PriceExtraOptionRow): void {
    const ref = this.dialog.open(ConfirmResetDialogComponent, {
      width: 'min(520px, 92vw)',
      data: {
        title: 'Slet price-extra?',
        message: `Vil du slette «${row.label}» fra kataloget?`,
        details: [
          'Eksisterende events påvirkes ikke.',
          'Deaktiver i stedet, hvis ekstra ikke skal vises i add/edit-event.',
        ],
        confirmText: 'Ja, slet',
        cancelText: 'Annuller',
        confirmColor: 'warn',
      },
    });
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.deletingId = row.id;
        this.errorMessage = null;
        this.http
          .delete(`${this.apiBase}/${row.id}`)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.deletingId = null;
              this.cdr.detectChanges();
            })
          )
          .subscribe({
            next: () => {
              this.tableDataSource.data = this.tableDataSource.data.filter(
                (x) => x.id !== row.id
              );
              delete this.labelDraft[row.id];
            },
            error: (error) => {
              this.errorMessage = this.formatApiError(
                error,
                'Kunne ikke slette price-extra.'
              );
            },
          });
      });
  }

  private formatApiError(error: unknown, fallback: string): string {
    const httpErr = error as { error?: unknown; message?: string };
    const body = httpErr?.error;
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as Record<string, unknown>;
        if (parsed && typeof parsed === 'object') {
          return this.formatApiError({ ...httpErr, error: parsed }, fallback);
        }
      } catch {
        return body.trim() || fallback;
      }
    }
    if (body && typeof body === 'object') {
      const o = body as { message?: string; error?: string; detail?: string };
      const tech = (o.detail || o.error || '').trim();
      const msg = (o.message || '').trim();
      if (tech && msg) return `${msg}: ${tech}`;
      return tech || msg || fallback;
    }
    return (httpErr?.message || '').trim() || fallback;
  }
}
